// Registra una vista individual de nota de forma idempotente
export const registerNoteView = async (noteId: string, userId?: string) => {
  // Intentamos un insert normal. Si hay error 409 (Conflicto/Duplicado), lo ignoramos.
  const { error } = await supabase
    .from('note_views')
    .insert({ note_id: noteId, user_id: userId || null });
  
  if (error) {
    // Código 409 o 23505 (unique_violation) -> Ignorar
    if (error.code === '409' || error.code === '23505') {
      return; 
    }
    console.error('Error registrando vista de nota:', error);
  }
};
import { supabase } from './client';
import { normalizeProfile } from './utils';
import { UserProfile, getProfileById } from './profiles'; // Import UserProfile and getProfileById
// Incrementa el contador de vistas de una nota
export const incrementNoteViewCount = async (noteId: string) => {
  // Leer el valor actual
  const { data, error: fetchError } = await supabase
    .from('notes')
    .select('view_count')
    .eq('id', noteId)
    .single();
  if (fetchError) {
    console.error('Error leyendo view_count en nota:', fetchError);
    return;
  }
  const current = data?.view_count ?? 0;
  const { error } = await supabase
    .from('notes')
    .update({ view_count: current + 1 })
    .eq('id', noteId);
  if (error) {
    console.error('Error incrementando view_count en nota:', error);
  }
};

export interface PromoItem {
  name: string;
  price: number;
  quantity?: number;
}

export interface NoteItem {
  id: string;
  user_id: string;
  title: string;
  content: string;
  photo_url: string | null;
  created_at: string;
  updated_at: string | null; // NEW
  is_edited: boolean; // NEW
  location_display_name: string | null;
  latitude: number | null;
  longitude: number | null;
  profiles: {
    username: string;
    avatar_url: string | null;
    display_name?: string | null;
    karma_points?: number;
  } | null;
  category_id: string | null;
  category_name: string | null;
  // DEALS/PROMOS fields
  store_name?: string | null;
  vendor_location?: {
    type: string;
    coordinates: [number, number];
  } | null;
  items?: PromoItem[] | null;
  external_url?: string | null;
  coupon_code?: string | null;
  is_affiliated?: boolean;
  is_freebie?: boolean;
  hotness_score?: number;
  upvotes?: number;
  downvotes?: number;
  start_at?: string | null;
  expiry_at?: string | null;
  deal_status?: 'active' | 'expired' | 'disputed' | 'hidden' | null;
  user_vote?: 'upvote' | 'downvote' | null; // El voto del usuario actual en este promo
}

export interface Category { // NEW INTERFACE
  id: string;
  name: string;
}

export const createNote = async (
  userId: string,
  title: string,
  content: string,
  photoUrl: string | null,
  imageUrls: string[] | null,
  locationDisplayName: string | null,
  latitude: number | null,
  longitude: number | null,
  categoryId: string | null,
  // DEALS/PROMOS fields
  storeName?: string | null,
  vendorLatitude?: number | null,
  vendorLongitude?: number | null,
  items?: PromoItem[] | null,
  externalUrl?: string | null,
  coupons?: Array<{ code: string; description?: string }> | null,
  isAffiliated?: boolean,
  isFreebee?: boolean,
  startAt?: string | null,
  expiryAt?: string | null
) => {
  
  const insertPayload: any = {
    user_id: userId,
    title,
    content,
    photo_url: photoUrl,
    image_urls: imageUrls,
    location_display_name: locationDisplayName,
    latitude,
    longitude,
    category_id: categoryId,
  };
  
  // Agregar campos de DEALS si están presentes
  if (storeName) insertPayload.store_name = storeName;
  if (vendorLatitude !== null && vendorLatitude !== undefined && vendorLongitude !== null && vendorLongitude !== undefined) {
    // Geography point en PostGIS: POINT(longitude latitude)
    insertPayload.vendor_location = `POINT(${vendorLongitude} ${vendorLatitude})`;
  }
  if (items && items.length > 0) insertPayload.items = items;
  if (externalUrl) insertPayload.external_url = externalUrl;
  if (coupons && coupons.length > 0) insertPayload.coupons = coupons;
  if (isAffiliated !== undefined) insertPayload.is_affiliated = isAffiliated;
  if (isFreebee !== undefined) insertPayload.is_freebie = isFreebee;
  if (startAt) insertPayload.start_at = startAt;
  if (expiryAt) insertPayload.expiry_at = expiryAt;
  
  
  const { data, error } = await supabase
    .from('notes')
    .insert(insertPayload)
    .select()
    .single();

  if (error) {
    console.error('[createNote ERROR] Error creating note:', error);
    console.error('[createNote ERROR] Failed payload was:', JSON.stringify(insertPayload, null, 2));
    throw error;
  }
  return data;
};

// NEW: Function to update a note
export const updateNote = async (
  noteId: string,
  userId: string,
  updates: {
    title: string;
    content: string;
    photo_url: string | null;
    image_urls?: string[] | null;
    location_display_name: string | null;
    latitude: number | null;
    longitude: number | null;
    category_id: string | null;
  }
) => {
  const { data, error } = await supabase
    .from('notes')
    .update(updates)
    .eq('id', noteId)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) {
    console.error('Error updating note:', error);
    throw error;
  }
  return data;
};


export const getNotesFromSubscribedUsers = async (currentUserId: string): Promise<NoteItem[]> => {
  const { data, error } = await supabase.rpc('get_notes_from_subscribed_users', { p_user_id: currentUserId });

  if (error) {
    console.error('Error fetching notes from subscribed users:', error);
    throw error;
  }
  
  
  const mapped = data.map((item: any) => ({
    id: item.id,
    user_id: item.user_id,
    title: item.title,
    content: item.content,
    photo_url: item.photo_url,
    image_urls: item.image_urls || [],
    created_at: item.created_at,
    location_display_name: item.location_display_name,
    latitude: item.latitude,
    longitude: item.longitude,
    view_count: item.view_count ?? 0,
    profiles: {
      username: item.profile_username,
      avatar_url: item.profile_avatar_url,
    },
  }));
  
  
  return mapped;
};

export const getUserNotesByUserId = async (userId: string): Promise<NoteItem[]> => {
  const { data, error } = await supabase
    .from('notes')
    .select('*, profiles(username,avatar_url,display_name,karma_points), categories(name)')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching user notes:', error);
    throw error;
  }
  return await Promise.all(data.map(async (item: any) => {
    let profiles = normalizeProfile(item.profiles);
    if (profiles && !('display_name' in profiles)) {
      try {
        const p = await getProfileById(item.user_id);
        if (p?.display_name) profiles.display_name = p.display_name;
      } catch (e) { /* ignore */ }
    }
    return {
      ...item,
      profiles,
      category_name: (item.categories as any)?.name || null,
    };
  }));
};

export const getNoteById = async (noteId: string): Promise<NoteItem | null> => {
  const { data, error } = await supabase
    .from('notes')
    .select('*, profiles(username,avatar_url,display_name,karma_points), categories(name)')
    .eq('id', noteId)
    .single();

  if (error) {
    console.error('Error fetching note by ID:', error);
    throw error;
  }
  if (!data) return null;
  const profiles = normalizeProfile(data.profiles);
  if (profiles && !('display_name' in profiles)) {
    try {
      const p = await getProfileById(data.user_id);
      if (p?.display_name) profiles.display_name = p.display_name;
    } catch (e) { /* ignore */ }
  }
  return {
    ...data,
    profiles,
    category_name: (data.categories as any)?.name || null,
  };
};

export const getNoteDetailsById = async (noteId: string) => {
  const { data, error } = await supabase
    .from('notes')
    .select('id,user_id,title,category_id,categories(name)') // ADD category_id and categories(name)
    .eq('id', noteId)
    .single();

  if (error) {
    console.error('Error fetching note details by ID:', error);
    return null;
  }
  return data ? {
    ...data,
    category_id: data.category_id,
    category_name: (data.categories as any)?.name || null,
  } : null;
};

export const deleteNote = async (noteId: string, userId: string) => {
  const { error } = await supabase
    .from('notes')
    .delete()
    .eq('id', noteId)
    .eq('user_id', userId);

  if (error) {
    console.error('Error deleting note:', error);
    throw error;
  }
};

export const getSingleLatestNoteForProfile = async (profileId: string): Promise<NoteItem | null> => {
  const { data, error } = await supabase.rpc('get_single_latest_note_for_profile', { p_profile_id: profileId });

  if (error) {
    // Si es 404 o PGRST116 (no rows), devolver null silenciosamente
    if (error.code === 'PGRST116' || error.status === 404) {
      return null;
    }
    console.error('Error fetching single latest note for profile:', error);
    throw error;
  }
  return data && data.length > 0 ? {
    ...data[0],
    profiles: normalizeProfile(data[0].profiles),
    category_id: data[0].category_id, // NEW
    category_name: data[0].category_name, // NEW
  } : null;
};

export const getNotesWithinRadius = async (
  latitude: number,
  longitude: number,
  radiusKm: number,
  keyword: string | null = null,
  categoryId: string | null = null,
) => {
  const rpcParams = {
    p_category_id: categoryId,
    p_keyword: keyword,
    p_latitude: latitude,
    p_longitude: longitude,
    p_radius_km: radiusKm,
    // Explicitly include exclude param (nullable) to disambiguate overloaded functions in the DB
    p_exclude_user_id: null,
  };


  // Log current auth session for debugging (helps detect if the client is authenticated)
  try {
    const authSession = await supabase.auth.getSession();
  } catch (sessErr) {
    console.warn('[RPC_CALL] Failed to get supabase auth session for debugging', sessErr);
  }

  const start = Date.now();
  const { data, error } = await supabase.rpc('get_notes_within_radius', rpcParams);
  const duration = Date.now() - start;

  if (error) {
    try {
      console.error('Error fetching notes within radius: (message, status, details)');
      // supabase-js error can have non-enumerable props; log common fields and a full stringify fallback
      console.error('message:', (error as any)?.message);
      console.error('status:', (error as any)?.status);
      console.error('details:', (error as any)?.details);
      console.error('hint:', (error as any)?.hint);
      console.error('Code/Message full JSON:', JSON.stringify(error, Object.getOwnPropertyNames(error), 2));
      console.dir(error);
    } catch (logErr) {
      console.error('Failed to pretty-log RPC error object', logErr, error);
    }
    // Helpful hint for ambiguous function overloads (PostgREST PGRST203)
    if ((error as any)?.code === 'PGRST203') {
      console.warn('[RPC_CALL] Detected PGRST203 (ambiguous function overload). Two options to fix:');
      console.warn('  1) Call the DB admin to DROP the older get_notes_within_radius overloads so only one signature remains.');
      console.warn('  2) Ensure RPC calls always pass all parameter names (including nullable p_exclude_user_id) to disambiguate.');
    }
    throw error;
  }
  // DEBUG: log raw RPC response to help troubleshoot client/server mismatch
  try {
  } catch (logErr) {
    console.warn('[RPC_CALL] Failed to stringify RPC response for logging', logErr);
  }
  
  // Fetch user profiles for each note to ensure we have complete profile data
  const notesWithProfiles = await Promise.all(
    data.map(async (item: any) => {
      let profiles = normalizeProfile(item.profiles);
      
      // If profiles are missing, fetch them separately
      if (!profiles && item.user_id) {
        const profile = await getProfileById(item.user_id);
        profiles = profile ? {
          username: profile.username,
          avatar_url: profile.avatar_url,
          display_name: profile.display_name,
          karma_points: profile.karma_points,
        } : null;
      } else if (profiles && !('display_name' in profiles) && item.user_id) {
        try {
          const profile = await getProfileById(item.user_id);
          if (profile?.display_name) profiles.display_name = profile.display_name;
        } catch (e) { /* ignore */ }
      }
      
      return {
        ...item,
        image_urls: item.image_urls || [],
        view_count: item.view_count ?? 0,
        profiles,
        category_id: item.category_id,
        category_name: item.category_name,
      };
    })
  );
  
  return notesWithProfiles;
};

// NEW: Function to get all categories
export const getAllCategories = async (): Promise<Category[]> => {
  const { data, error } = await supabase
    .from('categories')
    .select('id, name')
    .order('name', { ascending: true });

  if (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
  return data;
};

// NEW: Function to get notes by category
export const getNotesByCategory = async (categoryId: string, limit: number = 10): Promise<NoteItem[]> => {
  const { data, error } = await supabase
    .from('notes')
    .select('id,user_id,title,content,photo_url,image_urls,created_at,location_display_name,latitude,longitude,category_id,view_count,profiles(username,avatar_url),categories(name)')
    .eq('category_id', categoryId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error(`Error fetching notes for category ${categoryId}:`, error);
    throw error;
  }
  return data.map((item: any) => ({
    ...item,
    view_count: item.view_count ?? 0,
    profiles: normalizeProfile(item.profiles),
    category_id: item.category_id,
    category_name: (item.categories as any)?.name || null,
  }));
};

// ============================================================================
// DEALS/PROMOS VOTING SYSTEM
// ============================================================================

export interface NoteVote {
  id: string;
  note_id: string;
  user_id: string;
  vote_type: 'upvote' | 'downvote';
  created_at: string;
}

/**
 * Vote on a promo note (upvote or downvote)
 * If user already voted, updates the vote
 */
export const voteOnPromo = async (
  noteId: string,
  userId: string,
  voteType: 'upvote' | 'downvote'
): Promise<NoteVote> => {
  
  // Try to insert, if unique constraint fails, it means they already voted - then delete and re-insert
  const { data: insertData, error: insertError } = await supabase
    .from('note_votes')
    .insert({
      note_id: noteId,
      user_id: userId,
      vote_type: voteType,
    })
    .select()
    .single();

  if (insertError) {
    if (insertError.code === '23505') {
      // Unique constraint violation - user already voted
      
      // Delete old vote
      const { error: deleteError } = await supabase
        .from('note_votes')
        .delete()
        .eq('note_id', noteId)
        .eq('user_id', userId);

      if (deleteError) {
        console.error('[voteOnPromo] Error deleting old vote:', deleteError);
        throw deleteError;
      }

      // Insert new vote
      const { data: newVoteData, error: newVoteError } = await supabase
        .from('note_votes')
        .insert({
          note_id: noteId,
          user_id: userId,
          vote_type: voteType,
        })
        .select()
        .single();

      if (newVoteError) {
        console.error('[voteOnPromo] Error inserting new vote:', newVoteError);
        throw newVoteError;
      }

      return newVoteData;
    } else {
      console.error('[voteOnPromo] Error voting on promo:', insertError);
      throw insertError;
    }
  }

  return insertData;
};

/**
 * Remove vote from a promo note
 */
export const removeVoteFromPromo = async (
  noteId: string,
  userId: string
): Promise<void> => {
  
  const { error } = await supabase
    .from('note_votes')
    .delete()
    .eq('note_id', noteId)
    .eq('user_id', userId);

  if (error) {
    console.error('[removeVoteFromPromo] Error removing vote:', error);
    throw error;
  }
};

/**
 * Get user's vote on a specific promo note
 */
export const getUserVoteOnPromo = async (
  noteId: string,
  userId: string | null | undefined
): Promise<'upvote' | 'downvote' | null> => {
  if (!userId) return null;

  const { data, error } = await supabase
    .from('note_votes')
    .select('vote_type')
    .eq('note_id', noteId)
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('[getUserVoteOnPromo] Error fetching vote:', error);
    throw error;
  }

  return data?.vote_type || null;
};

/**
 * Get a promo note with full voting info
 */
export const getPromoWithVotes = async (
  noteId: string,
  userId: string | null | undefined
): Promise<NoteItem | null> => {
  const { data, error } = await supabase
    .from('notes')
    .select(
      `*,
       profiles:user_id(username, avatar_url, display_name, karma_points),
       categories:category_id(name)`
    )
    .eq('id', noteId)
    .single();

  if (error) {
    console.error('[getPromoWithVotes] Error fetching promo:', error);
    throw error;
  }

  // Get user's voting status only when we have a user id
  const userVote = userId ? await getUserVoteOnPromo(noteId, userId) : null;

  return {
    ...data,
    profiles: normalizeProfile(data.profiles),
    category_name: (data.categories as any)?.name || null,
    user_vote: userVote,
  } as NoteItem;
};

/**
 * Get active promos filtered by location and hotness
 */
export const getActivePromosByLocation = async (
  latitude: number,
  longitude: number,
  radiusKm: number = 5,
  userId: string | null | undefined,
  limit: number = 20
): Promise<NoteItem[]> => {

  // ST_DWithin expects distance in meters
  const radiusMeters = radiusKm * 1000;

  const { data, error } = await supabase
    .from('notes')
    .select(
      `*,
       profiles:user_id(username, avatar_url, display_name, karma_points),
       categories:category_id(name)`
    )
    .eq('deal_status', 'active')
    .eq('category_id', (await supabase
      .from('categories')
      .select('id')
      .eq('name', 'Promos')
      .single()
    ).data?.id)
    .filter('vendor_location', 'st_dwithin', `(${longitude}, ${latitude}), ${radiusMeters}`)
    .order('hotness_score', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('[getActivePromosByLocation] Error fetching promos:', error);
    throw error;
  }

  // Get user's votes for each promo
  if (!userId) {
    return data.map((note: any) => ({
      ...note,
      profiles: normalizeProfile(note.profiles),
      category_name: (note.categories as any)?.name || null,
      user_vote: null,
    }));
  }

  // Fetch votes for all promos
  const { data: votes, error: votesError } = await supabase
    .from('note_votes')
    .select('note_id, vote_type')
    .in(
      'note_id',
      data.map((n: any) => n.id)
    )
    .eq('user_id', userId);

  if (votesError) {
    console.error('[getActivePromosByLocation] Error fetching votes:', votesError);
  }

  const voteMap = new Map((votes || []).map((v: any) => [v.note_id, v.vote_type]));

  return data.map((note: any) => ({
    ...note,
    profiles: normalizeProfile(note.profiles),
    category_name: (note.categories as any)?.name || null,
    user_vote: voteMap.get(note.id) || null,
  }));
};

/**
 * Enrich notes with user votes
 * Takes an array of notes and adds user_vote field for each note
 */
export const enrichNotesWithUserVotes = async (
  notes: NoteItem[],
  userId: string | null | undefined
): Promise<NoteItem[]> => {
  if (!userId || notes.length === 0) {
    return notes;
  }

  const { data: votes, error: votesError } = await supabase
    .from('note_votes')
    .select('note_id, vote_type')
    .in(
      'note_id',
      notes.map((n) => n.id)
    )
    .eq('user_id', userId);

  if (votesError) {
    console.error('[enrichNotesWithUserVotes] Error fetching votes:', votesError);
    return notes;
  }

  const voteMap = new Map((votes || []).map((v: any) => [v.note_id, v.vote_type]));

  return notes.map((note) => ({
    ...note,
    user_vote: voteMap.get(note.id) || null,
  }));
};

/**
 * Ensure voting stats are present and valid (upvotes, downvotes, hotness)
 * Fetches stats from notes table directly
 */
export const enrichNotesWithVotingStats = async (
  notes: NoteItem[]
): Promise<NoteItem[]> => {
  if (notes.length === 0) {
    return notes;
  }

  // Fetch voting stats from notes table
  const { data: notesData, error: notesError } = await supabase
    .from('notes')
    .select('id, upvotes, downvotes, hotness_score')
    .in(
      'id',
      notes.map((n) => n.id)
    );

  if (notesError) {
    console.error('[enrichNotesWithVotingStats] Error fetching stats:', notesError);
    // Return with default values if error
    return notes.map((note) => ({
      ...note,
      upvotes: note.upvotes || 0,
      downvotes: note.downvotes || 0,
      hotness_score: note.hotness_score || 0,
    }));
  }

  // Create a map of stats by note id
  const statsMap = new Map(
    (notesData || []).map((n: any) => [
      n.id,
      {
        upvotes: n.upvotes ?? 0,
        downvotes: n.downvotes ?? 0,
        hotness_score: n.hotness_score ?? 0,
      },
    ])
  );

  // Update notes with stats from the map
  return notes.map((note) => {
    const stats = statsMap.get(note.id) || {
      upvotes: 0,
      downvotes: 0,
      hotness_score: 0,
    };
    
    return {
      ...note,
      upvotes: stats.upvotes,
      downvotes: stats.downvotes,
      hotness_score: stats.hotness_score,
    };
  });
};